import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { CheckboxChangeEvent, HcSort, HcTableDataSource, HcToastOptions, HcToasterService, PaginationComponent } from '@healthcatalyst/cashmere';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { OktaService } from '../../shared/services/okta.service';
import { environment } from '../../../environments/environment';

/** Admin Feedback */
@Component({
    selector: 'pd-admin-feedback',
    templateUrl: './admin-feedback.component.html',
    styleUrls: ['./admin-feedback.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminFeedbackComponent implements OnInit, AfterViewInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    dataSource: HcTableDataSource<any> = new HcTableDataSource();
    feedbackList: any[] = [];
    loading = true;
    loadError = false;
    respondedUpdating = false;
    adminFeedbackDisplayedColumns: string[] = [
        'date',
        'source',
        'helpful',
        'responded',
        'url',
        'suggestions',
        'email'
    ];
    adminFeedbackPageSize: number = 10;
    filterData: string[] = [];
    filterSelections: string[] = [];
    filterType = '';
    sourceFilters: string[] = [];
    helpfulFilters: string[] = [];
    sourceText = 'View All';
    helpfulText = 'View All';
    sourceKey: { [key: string]: string } = {};
    helpfulKey: { [key: string]: string } = {};

    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    get adminFeedbackListLength(): number {
        return this.dataSource.filteredData ? this.dataSource.filteredData.length : 0;
    }

    constructor(
        private http: HttpClient,
        private oktaService: OktaService,
        private toasterService: HcToasterService
    ) { }

    ngOnInit(): void {
        this.loadFeedback();
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.initSorting();
    }

    loadFeedback(): void {
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        this.http.get<any>( environment.azure.url + '/admin.feedback', options)
            .subscribe({
                next: data => {
                    this.feedbackList = data;

                    for (let index = 0; index < this.feedbackList.length; index++) {
                        this.feedbackList[index].helpful = this.feedbackList[index].helpful ? 'Yes' : 'No';
                    }

                    this.dataSource = new HcTableDataSource(this.feedbackList);
                
                    if (this.sort && this.paginator) {
                        this.dataSource.sort = this.sort;
                        this.dataSource.paginator = this.paginator;
                        this.initSorting();
                    }

                    this.loading = false;
                    this.loadError = false;
                },
                error: msg => {
                    this.loading = false;
                    this.loadError = true;
                    console.log(msg);
                }
            });
    }

    initSorting(): void {
        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {     
                case 'date': 
                    const sortDate = new Date(item.date);
                    return sortDate.getTime();
                case 'responded':
                    return item.responded === true;
                default: {
                    return item[property];
                }
            }
        };
    }

    applySearchFilter(filter: string): void {
        this.dataSource.filter = filter.trim().toLowerCase();
    }

    storeSelectedTextValue(filterArray: string[]) {
        switch (this.filterType) {
            case 'source':
                if (filterArray.length == 1) this.sourceText = filterArray[0];
                else if (filterArray.length > 1) this.sourceText = `${filterArray.length} selected`;
                else this.sourceText = 'View All';
                break;
            case 'helpful':
                if (filterArray.length == 1) this.helpfulText = filterArray[0];
                else if (filterArray.length > 1) this.helpfulText = `${filterArray.length} selected`;
                else this.helpfulText = 'View All';
                break;
            default:
                break;
        }
    }

    storeFilterData(type: string) {
        let filterData: string[] = [];
        this.filterType = type;

        for (let i = 0; i < this.feedbackList.length; i++) {
            if (this.feedbackList[i][type]) {
                if (!filterData.includes(this.feedbackList[i][type])) {
                    filterData.push(this.feedbackList[i][type]);
                }
            }
        }
        this.filterData = filterData;
    }

    filterSources() {
        let filteredSources = [];

        for (let i = 0; i < this.feedbackList.length; i++) {
            let sourceFilterMatch = this.sourceFilters.length === 0;
            if (this.sourceFilters.includes(this.feedbackList[i]['source'])) {
                sourceFilterMatch = true;
            }

            let helpfulFilterMatch = this.helpfulFilters.length === 0;
            if (this.helpfulFilters.includes(this.feedbackList[i]['helpful'])) {
                helpfulFilterMatch = true;
            }

            if (sourceFilterMatch && helpfulFilterMatch) {
                filteredSources.push(this.feedbackList[i]);
            }
        }

        //grab filter that was applied from text
        let textFilter = this.dataSource.filter;

        //set data source to the filtered technology list
        this.dataSource = new HcTableDataSource(filteredSources);

        //reset paginator and sort
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        //add the text filter
        this.dataSource.filter = textFilter;
    }

    storeFilterArray(filterArray: string[]) {
        this.storeSelectedTextValue(filterArray);
        
        if (this.filterType === 'source') {
            this.sourceFilters = filterArray;
        } else if (this.filterType === 'helpful') {
            this.helpfulFilters = filterArray;
        }

        this.filterSources();
    }

    checkEvent( event: CheckboxChangeEvent, element: any ): void {
        const saveObject = {
            "feedbackID": element.feedbackID,
            "source": element.source,
            "helpful": element.helpful === 'Yes',
            "responded": event.checked,
            "email": element.email,
            "suggestions": element.suggestions,
            "url": element.url,
            "date": element.date
        };
        this.respondedUpdating = true;

        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        this.http.post<any>( environment.azure.url + '/admin.feedback.update', saveObject, options)
            .subscribe({
                next: data => {
                    const options: HcToastOptions = {
                        header: 'Feedback Updated',
                        body: 'Responded state has been updated.',
                        type: 'success',
                        position: 'bottom-right'
                    };

                    this.toasterService.addToast(options);
                    this.respondedUpdating = false;
                },
                error: msg => {
                    const options: HcToastOptions = {
                        header: 'Update Failed',
                        body: 'Unable to update responded state. Try again later.',
                        type: 'alert',
                        position: 'bottom-right'
                    };

                    this.toasterService.addToast(options);
                    this.respondedUpdating = false;
                    console.log(msg);
                }
            });
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
